import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { Calendar } from '@/components/ui/calendar';
import { ArrowLeft, ArrowRight, Check, Wrench, MapPin, CalendarDays, CreditCard, Banknote, Loader2, Plus, Minus, X, DoorOpen, LayoutGrid, Camera, Clock, Euro, Phone, Search, ChevronDown, ChevronUp, Mic, Square, Trash2, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO, isSameDay } from 'date-fns';
import { it } from 'date-fns/locale';

const STEPS = ['Intervento', 'Prenotazione', 'Riepilogo e Pagamento'];
const ZONES = ['Brescia', 'Verona', 'Mantova', 'Cremona'];
const HOW_IT_WORKS = [
  { icon: Phone, title: 'Richiesta Online', desc: 'Ci descrivi il problema, anche con un messaggio vocale, e scegli data e orario.', c: 'bg-blue-50 border-blue-200', ic: 'bg-blue-600', t: 'text-blue-800' },
  { icon: Search, title: 'Diagnosi sul Posto', desc: 'Il tecnico individua la causa: cerniere, maniglie, guarnizioni, vetri, tapparelle.', c: 'bg-amber-50 border-amber-200', ic: 'bg-amber-500', t: 'text-amber-800' },
  { icon: Wrench, title: 'Riparazione', desc: 'Interveniamo subito quando possibile, oppure ti proponiamo un preventivo dedicato.', c: 'bg-emerald-50 border-emerald-200', ic: 'bg-emerald-600', t: 'text-emerald-800' },
  { icon: Euro, title: 'Pagamento al Tecnico', desc: 'Uscita EUR 25 + EUR 40/h di lavoro. Paghi a fine intervento: contanti, POS o bonifico.', c: 'bg-rose-50 border-rose-200', ic: 'bg-rose-500', t: 'text-rose-800' },
];
const FAQS = [
  { q: 'Quanto costa un intervento a chiamata?', a: 'Il costo di uscita è di EUR 25 più il rimborso chilometrico di EUR 0,50/km (andata e ritorno da Castiglione delle Stiviere), a cui si aggiungono EUR 40 per ogni ora di lavoro. Eventuali pezzi di ricambio vengono concordati prima.' },
  { q: 'Che tipo di problemi risolvete?', a: 'Finestre che non chiudono, porte che strisciano, maniglie rotte, cerniere fuori asse, guarnizioni consumate, tapparelle bloccate, spifferi e infiltrazioni.' },
  { q: 'E se serve un pezzo di ricambio?', a: 'Se il pezzo non è disponibile sul momento, il tecnico ti propone un preventivo e fissiamo un secondo intervento. Nessun lavoro extra senza il tuo consenso.' },
  { q: 'In quali zone operate?', a: 'Operiamo nelle province di Brescia, Verona, Mantova e Cremona, con sede a Castiglione delle Stiviere (MN).' },
];
const SectionTitle = ({ icon: Icon, color, children }) => (
  <h2 className="text-base md:text-lg font-extrabold text-slate-900 flex items-center gap-2">
    <span className={`w-7 h-7 rounded-lg ${color} text-white flex items-center justify-center`}><Icon className="w-4 h-4" /></span>
    {children}
  </h2>
);
const PROVINCE_LABEL = { brescia: 'Brescia', verona: 'Verona', mantova: 'Mantova', cremona: 'Cremona' };

export default function QuoteWizard() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [config, setConfig] = useState(null);

  // Step 1
  const [problema, setProblema] = useState('');
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioId, setAudioId] = useState('');
  const [recording, setRecording] = useState(false);
  const recorderRef = useRef(null);
  const [photos, setPhotos] = useState([]);
  const [nome, setNome] = useState('');
  const [cognome, setCognome] = useState('');
  const [codiceFiscale, setCodiceFiscale] = useState('');
  const [telefono, setTelefono] = useState('');
  const [emailCliente, setEmailCliente] = useState('');
  const [address, setAddress] = useState('');
  const [provincia, setProvincia] = useState('');
  const [faqOpen, setFaqOpen] = useState(null);

  const PROVINCE_DISPONIBILI = ['Brescia', 'Cremona', 'Mantova', 'Verona'];

  // Step 2
  const [slots, setSlots] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState('');
  const [loadingSlots, setLoadingSlots] = useState(false);

  // Distance
  const [distanceInfo, setDistanceInfo] = useState(null);
  const [loadingDistance, setLoadingDistance] = useState(false);

  // Quote creation state
  const [quoteId, setQuoteId] = useState(null);
  const [creatingQuote, setCreatingQuote] = useState(false);

  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setQuoteId(null);
  }, [problema, audioBlob, photos, nome, cognome, codiceFiscale, telefono, emailCliente, address, provincia]);

  useEffect(() => {
    api.get('/pricing/config').then((r) => setConfig(r.data)).catch(() => toast.error('Errore configurazione'));
  }, []);

  // Load slots (no zone filter)
  const loadSlots = (prov) => {
    setLoadingSlots(true);
    setSlots([]);
    setSelectedDate(null);
    setSelectedSlot('');
    const params = new URLSearchParams({ service_type: 'una_tantum' });
    if (prov) params.append('province', prov);
    api.get(`/appointments/slots?${params}`)
      .then((r) => setSlots(r.data))
      .catch(() => toast.error('Errore caricamento slot'))
      .finally(() => setLoadingSlots(false));
  };

  // Calculate distance when moving to step 2
  const goToStep2 = async () => {
    if (!problema.trim() && !audioBlob) { toast.error('Scrivici il problema o registra un audio'); return; }
    if (recording) { toast.error('Ferma prima la registrazione audio'); return; }
    if (!nome || !cognome) { toast.error('Inserisci nome e cognome'); return; }
    if (!codiceFiscale.trim() || codiceFiscale.trim().length < 16) { toast.error('Inserisci il codice fiscale (16 caratteri)'); return; }
    if (!telefono.trim()) { toast.error('Inserisci il numero di telefono'); return; }
    if (!address.trim()) { toast.error('Inserisci l\'indirizzo'); return; }
    if (!provincia) { toast.error('Seleziona la provincia'); return; }
    if (address.trim()) {
      setLoadingDistance(true);
      try {
        const res = await api.post('/distance/calculate', { address });
        setDistanceInfo(res.data);
      } catch { setDistanceInfo(null); }
      finally { setLoadingDistance(false); }
    }
    loadSlots(provincia);
    setStep(1);
  };

  const availableDates = slots.map((s) => parseISO(s.date));
  const getSlots = (date) => {
    if (!date) return [];
    const found = slots.find((s) => s.date === format(date, 'yyyy-MM-dd'));
    return found ? found.available_slots : [];
  };

  const costoChiamata = 25;
  const tariffaOraria = 40;
  const surcharge = distanceInfo?.surcharge || 0;

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      const chunks = [];
      rec.ondataavailable = (e) => chunks.push(e.data);
      rec.onstop = () => {
        const blob = new Blob(chunks, { type: rec.mimeType || 'audio/webm' });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        setAudioId('');
        stream.getTracks().forEach((t) => t.stop());
      };
      recorderRef.current = rec;
      rec.start();
      setRecording(true);
    } catch { toast.error('Microfono non disponibile'); }
  };
  const stopRecording = () => { recorderRef.current?.stop(); setRecording(false); };
  const deleteAudio = () => { setAudioBlob(null); setAudioUrl(null); setAudioId(''); };

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    if (photos.length + files.length > 5) { toast.error('Massimo 5 foto'); return; }
    files.forEach((file) => { const r = new FileReader(); r.onload = () => setPhotos((p) => [...p, r.result]); r.readAsDataURL(file); });
  };
  const removePhoto = (idx) => setPhotos(photos.filter((_, i) => i !== idx));

  // Create quote when entering step 3 (appointment created only on confirm)
  const goToStep3 = async () => {
    if (quoteId) { setStep(2); return; }
    setCreatingQuote(true);
    try {
      let aid = audioId;
      if (audioBlob && !aid) {
        const fd = new FormData();
        fd.append('file', audioBlob, 'problema.webm');
        const up = await api.post('/uploads/audio', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        aid = up.data.audio_id;
        setAudioId(aid);
      }
      const res = await api.post('/quotes', {
        service_type: 'una_tantum',
        fixtures: [],
        fixture_details: [],
        problema,
        audio_id: aid,
        photos, address, zone: '',
        nome, cognome, telefono, email_cliente: emailCliente,
        codice_fiscale: codiceFiscale,
        notes: problema,
        fonte: localStorage.getItem('regola_fonte') || '',
      });
      setQuoteId(res.data.quote_id);
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nella creazione');
    } finally {
      setCreatingQuote(false);
    }
  };

  if (!config) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-stone-50">
        <div className="w-8 h-8 border-3 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50" data-testid="quote-wizard-page">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-3xl mx-auto px-4 h-16 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => step > 0 ? setStep(step - 1) : navigate('/dashboard')} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <p className="text-sm font-bold text-slate-900">Intervento a Chiamata</p>
            <p className="text-xs text-slate-400">Passo {step + 1} di {STEPS.length}</p>
          </div>
          <div className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-xs font-bold">
            Pagamento al tecnico
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8">
        <div className="flex items-center justify-center gap-2 mb-8">
          {STEPS.map((s, i) => (
            <div key={i} className="flex items-center gap-2">
              <div className={`step-dot ${i < step ? 'completed' : i === step ? 'active' : 'pending'}`}>
                {i < step ? <Check className="w-4 h-4" /> : i + 1}
              </div>
              {i < STEPS.length - 1 && <div className={`w-8 h-0.5 ${i < step ? 'bg-emerald-500' : 'bg-slate-200'}`} />}
            </div>
          ))}
        </div>

        {/* ── STEP 0: Intervento ── */}
        {step === 0 && (
          <div className="space-y-5" data-testid="step-intervento">
            <div className="rounded-2xl bg-slate-900 text-white p-6 sm:p-8 relative overflow-hidden" data-testid="chiamata-hero">
              <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-amber-400/20" />
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-amber-400 mb-2">Riparazione serramenti a domicilio</p>
              <h1 className="text-2xl sm:text-3xl font-extrabold leading-tight" style={{ fontFamily: "'Manrope', sans-serif" }}>
                Intervento <span className="text-amber-400">a Chiamata</span>
              </h1>
              <p className="text-sm text-slate-300 mt-2 max-w-lg">Hai una finestra o una porta che non funziona? Scrivici il problema o mandaci un audio: il tecnico valuterà il lavoro direttamente sul posto.</p>
              <div className="flex flex-wrap items-center gap-3 mt-5">
                <span className="bg-amber-400 text-slate-900 text-sm font-extrabold px-4 py-1.5 rounded-full" data-testid="hero-price">Uscita EUR {costoChiamata}</span>
                <span className="bg-white/10 text-white text-sm font-bold px-4 py-1.5 rounded-full">+ EUR {tariffaOraria}/h</span>
                <span className="text-xs text-slate-400">Pagamento al tecnico a fine intervento</span>
              </div>
            </div>

            <div>
              <div className="mb-3"><SectionTitle icon={Wrench} color="bg-blue-600">Come funziona</SectionTitle></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {HOW_IT_WORKS.map((s, i) => (
                  <div key={i} className={`flex items-start gap-3 rounded-xl border p-4 ${s.c} transition-transform duration-200 hover:-translate-y-0.5`} data-testid={`how-${i}`}>
                    <span className={`w-9 h-9 rounded-lg ${s.ic} text-white flex items-center justify-center flex-shrink-0`}><s.icon className="w-4 h-4" /></span>
                    <div>
                      <p className={`text-sm font-extrabold ${s.t}`}>{s.title}</p>
                      <p className="text-xs text-slate-600 leading-relaxed mt-0.5">{s.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap items-center gap-2 mt-4">
                <span className="text-xs text-slate-400 mr-1">Zone servite:</span>
                {ZONES.map((p) => (
                  <span key={p} className="inline-flex items-center gap-1 bg-white text-slate-600 text-xs px-2.5 py-1 rounded-full border border-slate-200">
                    <MapPin className="w-3 h-3 text-blue-400" />{p}
                  </span>
                ))}
              </div>
            </div>

            <div data-testid="problema-section">
              <SectionTitle icon={MessageSquare} color="bg-blue-600">Scrivici il tuo problema da risolvere</SectionTitle>
              <p className="text-xs text-slate-500 mt-1 mb-3 ml-9">Descrivi cosa non funziona oppure, se preferisci, mandaci un messaggio vocale.</p>
              <Textarea value={problema} onChange={(e) => setProblema(e.target.value)}
                placeholder="Es: la porta-finestra del soggiorno non si chiude bene e fa spifferi, la tapparella della camera è bloccata..."
                className="rounded-xl bg-white" rows={5} data-testid="problema-input" />
              <div className="mt-3 rounded-xl border border-slate-200 bg-white p-4 flex flex-wrap items-center gap-3" data-testid="audio-box">
                <span className="text-sm font-semibold text-slate-700 mr-auto flex items-center gap-2"><Mic className="w-4 h-4 text-rose-500" /> Oppure manda un audio</span>
                {!recording && !audioUrl && (
                  <Button type="button" onClick={startRecording} className="rounded-full bg-rose-500 hover:bg-rose-600 text-white" data-testid="audio-record-btn">
                    <Mic className="w-4 h-4 mr-2" /> Registra
                  </Button>
                )}
                {recording && (
                  <Button type="button" onClick={stopRecording} className="rounded-full bg-slate-900 hover:bg-slate-700 text-white animate-pulse" data-testid="audio-stop-btn">
                    <Square className="w-4 h-4 mr-2" /> Ferma registrazione
                  </Button>
                )}
                {audioUrl && !recording && (
                  <div className="flex items-center gap-2 w-full sm:w-auto">
                    <audio controls src={audioUrl} className="h-10" data-testid="audio-preview" />
                    <Button type="button" variant="ghost" size="icon" onClick={deleteAudio} className="text-slate-400 hover:text-red-500" data-testid="audio-delete-btn">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
            </div>

            <div>
              <Label className="text-sm font-semibold text-slate-700 mb-3 block">Foto (opzionale)</Label>
              <div className="dropzone p-5 text-center cursor-pointer rounded-xl" onClick={() => document.getElementById('photo-input').click()} data-testid="photo-dropzone">
                <Camera className="w-7 h-7 text-slate-400 mx-auto mb-1" />
                <p className="text-sm text-slate-500">Clicca per caricare foto</p>
                <p className="text-xs text-slate-400">Max 5 foto</p>
                <input id="photo-input" type="file" accept="image/*" multiple className="hidden" onChange={handlePhotoUpload} />
              </div>
              {photos.length > 0 && (
                <div className="flex gap-3 mt-3 flex-wrap">
                  {photos.map((p, i) => (
                    <div key={i} className="relative w-16 h-16 rounded-xl overflow-hidden group">
                      <img src={p} alt={`foto-${i}`} className="w-full h-full object-cover" />
                      <button onClick={() => removePhoto(i)} className="absolute top-0.5 right-0.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <h2 className="text-base md:text-lg font-extrabold text-slate-900 mb-3 flex items-center gap-2">
                <span className="w-7 h-7 rounded-lg bg-violet-600 text-white flex items-center justify-center font-black text-sm">?</span>
                Domande frequenti
              </h2>
              <div className="space-y-2">
                {FAQS.map((faq, i) => (
                  <Card key={i} className={`border-0 shadow-sm rounded-xl overflow-hidden cursor-pointer border-l-4 ${faqOpen === i ? 'border-l-violet-600 bg-violet-50/40' : 'border-l-transparent'}`} onClick={() => setFaqOpen(faqOpen === i ? null : i)} data-testid={`faq-${i}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <p className={`text-sm font-bold flex-1 pr-2 ${faqOpen === i ? 'text-violet-700' : 'text-slate-800'}`}>{faq.q}</p>
                        {faqOpen === i ? <ChevronUp className="w-4 h-4 text-slate-400 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0" />}
                      </div>
                      {faqOpen === i && <p className="text-sm text-slate-600 leading-relaxed mt-3 pt-3 border-t border-violet-100">{faq.a}</p>}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            <Separator />
            <SectionTitle icon={Check} color="bg-emerald-600">I tuoi dati</SectionTitle>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Nome *</Label>
                <Input value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Il tuo nome" className="rounded-xl" data-testid="nome-input" />
              </div>
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Cognome *</Label>
                <Input value={cognome} onChange={(e) => setCognome(e.target.value)} placeholder="Il tuo cognome" className="rounded-xl" data-testid="cognome-input" />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs text-slate-500 mb-1 block">Codice Fiscale *</Label>
                <Input value={codiceFiscale} onChange={(e) => setCodiceFiscale(e.target.value.toUpperCase())} placeholder="RSSMRA85M01H501Z" className="rounded-xl font-mono" maxLength={16} data-testid="cf-input" />
                <p className="text-xs text-slate-400 mt-1">Serve per effettuare la fattura</p>
              </div>
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Telefono *</Label>
                <Input value={telefono} onChange={(e) => setTelefono(e.target.value)} placeholder="+39 ..." type="tel" className="rounded-xl" data-testid="telefono-input" />
              </div>
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Email</Label>
                <Input value={emailCliente} onChange={(e) => setEmailCliente(e.target.value)} placeholder="email@esempio.it" type="email" className="rounded-xl" data-testid="email-input" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Indirizzo *</Label>
                <Input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Via, numero, città" className="rounded-xl" data-testid="address-input" />
                <p className="text-xs text-slate-400 mt-1">
                  <MapPin className="w-3 h-3 inline mr-1" />
                  Serve per calcolare la distanza dalla nostra sede
                </p>
              </div>
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Provincia *</Label>
                <select
                  value={provincia}
                  onChange={(e) => setProvincia(e.target.value)}
                  className="flex h-10 w-full rounded-xl border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  data-testid="provincia-select"
                >
                  <option value="">Seleziona provincia</option>
                  {PROVINCE_DISPONIBILI.map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex justify-end pt-4">
              <Button className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-8" onClick={goToStep2} data-testid="next-step-btn">
                {loadingDistance ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Avanti <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* ── STEP 1: Prenotazione ── */}
        {step === 1 && (
          <div className="space-y-6" data-testid="step-prenotazione">
            <div className="rounded-2xl bg-slate-900 text-white p-6 relative overflow-hidden" data-testid="chiamata-booking-hero">
              <div className="absolute -right-10 -top-10 w-32 h-32 rounded-full bg-amber-400/20" />
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-amber-400 mb-1">Passo 2 · Prenotazione</p>
              <h1 className="text-xl sm:text-2xl font-extrabold" style={{ fontFamily: "'Manrope', sans-serif" }}>Scegli quando <span className="text-amber-400">passiamo da te</span></h1>
              <p className="text-sm text-slate-300 mt-1">{nome} {cognome}{provincia ? ` · ${provincia}` : ''}</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 flex items-start gap-3" data-testid="distance-card">
                <span className="w-9 h-9 rounded-lg bg-emerald-600 text-white flex items-center justify-center flex-shrink-0"><MapPin className="w-4 h-4" /></span>
                <div className="text-sm">
                  <p className="font-extrabold text-emerald-800">{distanceInfo?.distance_km ? `${distanceInfo.distance_km} km dalla sede` : 'Distanza non calcolata'}</p>
                  <p className="text-xs text-slate-600 mt-0.5">da {config.base_location}{provincia ? ` · Provincia di ${provincia}` : ''}</p>
                </div>
              </div>
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 flex items-start gap-3" data-testid="cost-card">
                <span className="w-9 h-9 rounded-lg bg-amber-500 text-white flex items-center justify-center flex-shrink-0"><Euro className="w-4 h-4" /></span>
                <div className="text-sm">
                  <p className="font-extrabold text-amber-800">Uscita EUR {costoChiamata + surcharge} + EUR {tariffaOraria}/h</p>
                  <p className="text-xs text-slate-600 mt-0.5">{surcharge > 0 ? `Include EUR ${surcharge} di rimborso km A/R (EUR 0,50/km). ` : ''}Pagamento al tecnico</p>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-xs text-blue-800">
              <strong>Come funziona:</strong> il calendario mostra solo i giorni in cui il tecnico opera nella tua provincia. Disponibilità dal Lunedì al Venerdì.
            </div>

            <SectionTitle icon={CalendarDays} color="bg-blue-600">Scegli data e orario</SectionTitle>

            {loadingSlots ? (
              <div className="flex justify-center py-12">
                <div className="w-8 h-8 border-3 border-blue-600 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card className="border-0 shadow-sm rounded-xl">
                  <CardContent className="p-4">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={(d) => { setSelectedDate(d); setSelectedSlot(''); }}
                      locale={it}
                      disabled={(date) => {
                        if (date < new Date()) return true;
                        return !availableDates.some((ad) => isSameDay(ad, date));
                      }}
                      modifiers={{ available: availableDates }}
                      modifiersStyles={{ available: { fontWeight: 'bold', color: '#2563eb' } }}
                      className="w-full"
                      data-testid="calendar-chiamata"
                    />
                  </CardContent>
                </Card>
                <div>
                  {selectedDate ? (
                    <Card className="border-0 shadow-sm rounded-xl">
                      <CardContent className="p-5 space-y-2">
                        <p className="text-sm font-bold text-slate-900 flex items-center gap-2 mb-3">
                          <CalendarDays className="w-4 h-4 text-blue-600" />
                          {format(selectedDate, 'EEEE d MMMM', { locale: it })}
                        </p>
                        {getSlots(selectedDate).map((slot) => (
                          <div
                            key={slot}
                            className={`slot-available ${selectedSlot === slot ? 'selected' : ''}`}
                            onClick={() => setSelectedSlot(slot)}
                            data-testid={`slot-${slot}`}
                          >
                            <span className="font-medium text-sm">{slot}</span>
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                  ) : (
                    <Card className="border-0 shadow-sm rounded-xl">
                      <CardContent className="p-8 text-center">
                        <CalendarDays className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                        <p className="text-sm text-slate-500">Seleziona una data</p>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </div>
            )}

            <div className="flex justify-between pt-4">
              <Button variant="outline" className="rounded-full" onClick={() => setStep(0)}>
                <ArrowLeft className="w-4 h-4 mr-2" /> Indietro
              </Button>
              <Button className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-8"
                disabled={!selectedDate || !selectedSlot || creatingQuote}
                onClick={goToStep3} data-testid="next-step-btn">
                {creatingQuote ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Avanti <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* ── STEP 2: Riepilogo e Pagamento ── */}
        {step === 2 && (
          <div className="space-y-6" data-testid="step-pagamento">
            <Card className="border-0 shadow-sm rounded-xl overflow-hidden">
              <div className="bg-slate-900 text-white p-6">
                <p className="text-sm text-slate-400 mb-1">Costi Intervento a Chiamata</p>
              </div>
              <CardContent className="p-5 space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600 flex items-center gap-2">
                    <Phone className="w-4 h-4 text-blue-500" /> Costo chiamata
                  </span>
                  <span className="font-bold text-slate-900">EUR 25</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600 flex items-center gap-2">
                    <Clock className="w-4 h-4 text-blue-500" /> Manodopera (min. 1 ora)
                  </span>
                  <span className="font-bold text-slate-900">EUR 40/h</span>
                </div>
                <Separator />
                <p className="text-xs text-slate-500">
                  Rimborso kilometrico in base alla sede.
                </p>
                <p className="text-xs text-slate-400">
                  Il costo finale dipende dalla durata dell'intervento. Il tecnico valuterà sul posto.
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm rounded-xl">
              <CardContent className="p-5">
                <p className="text-xs text-slate-400 uppercase tracking-wider mb-2 font-semibold">Appuntamento</p>
                <p className="text-sm font-semibold text-slate-900">
                  {selectedDate && format(selectedDate, 'EEEE d MMMM yyyy', { locale: it })} - {selectedSlot}
                </p>
                <Separator className="my-3" />
                <p className="text-xs text-slate-400 uppercase tracking-wider mb-2 font-semibold">Cliente</p>
                <p className="text-sm text-slate-900 font-medium">{nome} {cognome}</p>
                {telefono && <p className="text-xs text-slate-500">{telefono}</p>}
                {address && <p className="text-xs text-slate-500">{address}</p>}
                <Separator className="my-3" />
                <p className="text-xs text-slate-400 uppercase tracking-wider mb-2 font-semibold">Problema da risolvere</p>
                {problema && <p className="text-sm text-slate-700 whitespace-pre-line" data-testid="summary-problema">{problema}</p>}
                {audioUrl && <audio controls src={audioUrl} className="h-10 mt-2" data-testid="summary-audio" />}
              </CardContent>
            </Card>

            {/* Payment Options - Only pay at technician */}
            <Card className="border-0 shadow-sm rounded-xl bg-emerald-50">
              <CardContent className="p-5">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
                    <Euro className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-slate-900">Pagamento al tecnico</p>
                    <p className="text-xs text-slate-600 mt-1">
                      Potrai pagare direttamente al tecnico una volta finito l'intervento. Accettiamo contanti, POS e bonifico.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Button
              className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-full py-6 text-base font-semibold"
              onClick={async () => {
                setSubmitting(true);
                try {
                  await api.post('/appointments', {
                    quote_id: quoteId,
                    date: format(selectedDate, 'yyyy-MM-dd'),
                    time_slot: selectedSlot,
                    zone: '',
                    province: provincia,
                    notes: 'Intervento a Chiamata',
                  });
                  toast.success('Prenotazione confermata!');
                  navigate('/');
                } catch (err) { toast.error(err.response?.data?.detail || 'Errore conferma'); }
                finally { setSubmitting(false); }
              }}
              disabled={submitting}
              data-testid="confirm-booking-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
              Conferma Prenotazione
            </Button>

            <div className="pt-2">
              <Button variant="outline" className="rounded-full w-full" onClick={() => setStep(1)}>
                <ArrowLeft className="w-4 h-4 mr-2" /> Modifica
              </Button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
